import React, {useEffect, useState} from 'react';
import useStyles from "../styles/styles";
import getEnvironment from "../utils/getEnvironment";
import useFormInputs from "../hooks/useFormInputs";
import {useMutation, useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Grid,
    Modal,
    Paper,
    Typography
} from "@material-ui/core";
import {Alert} from "@material-ui/lab";
import {navigate} from "@reach/router";
import useTrafficStyles from "../styles/trafficStyles";
import PanelBox from "../components/TrafficSource/PanelBox";
import {pixelMap, pixelQueryParametersMap, pixelSubscribersMap} from "../utils/PixelMap";
import _ from "lodash";
import DeleteIcon from "@material-ui/icons/Delete";
import ExpanderList from "../components/Common/ExpanderList";
import dashes from "add-dashes-to-uuid";
import RowChanges from "../components/Common/RowChanges";

const Pixel = ({pixelId}) => {
    const classes = useStyles();
    const trafficClasses = useTrafficStyles();
    const env = getEnvironment();
    const [editView, setEditView] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [isError, setIsError] = useState(false);
    const [defaultValues, setDefaultValues] = useState({});
    const [isModalOpen, setIsModalOpen] = useState('');
    const [queryParameters, setQueryParameters] = useState([]);
    const [subscribers, setSubscribers] = useState([]);
    const [manualLoad, setManualLoad] = useState(false);
    const id = pixelId.includes('-') ? pixelId : dashes(pixelId);

    const onSubmit = () => {
        let error = false;
        setIsError(error);
        let nonRequiredFields = [];
        pixelMap.forEach((section) => {
            section.arr.forEach((item) => {
                if (!item.required) {
                    nonRequiredFields.push(item.key);
                }
            });
        });
        Object.keys(inputs).forEach(key => {
            if (nonRequiredFields.includes(key)) {
                return
            }
            const value = inputs[key]
            if (value === '') {
                console.error(`${key} is a required field.`)
                error = true
            }
        });

        if (!error) {
            openModal('edit');
        } else {
            setIsError(true)
        }
    };

    const {
        clearAllInputs,
        inputs,
        handleSwitchChange,
        handleFilterChange,
        handleInputChange,
        handleSubmit,
        setInputs
    } = useFormInputs(onSubmit);

    const openModal = (type) => {
        setIsModalOpen(type);
    };
    const closeModal = () => {
        setIsModalOpen('');
    };

    const {loading, error, data, refetch} = useQuery(gql`
        query PixelPage($id: UUID!) {
            pixel(id: $id){
                pixelName
                pixelKey
                httpMethod
                id
                pixelQueryParameters {
                    id
                    pixelId
                    parameterName
                    sourceData
                    constantValue
                }
                pixelSubscribers {
                    id
                    pixelId
                    affiliateId
                    trafficSourceId
                    revenueType
                }
                url
            }
            allAffiliate {
                nodes {
                    id
                    name
                }
            }
            allTrafficSource {
                nodes {
                    id
                    name
                }
            }
            allPixelHttpMethods: __type(name: "PixelHttpMethod") {
                enumValues {
                    name
                }
            }
            allRevenueTypes: __type(name: "RevenueType") {
                enumValues {
                    name
                }
            }
            allPixelQueryParameterSourceData: __type(name: "PixelQueryParameterSourceData") {
                enumValues {
                    name
                }
            }
        }
    `, {
        variables: {
            id: pixelId
        },
        notifyOnNetworkStatusChange: true,
        context: {
            clientName: "kgraphql"
        }
    });

    const [createPixel, {data: mutationData, loading: mutationLoading}] = useMutation(gql`
        mutation CreatePixel($input: PixelMutatorInput!) {
            createPixel(input: $input) {
                pixelName
                pixelKey
                httpMethod
                id
                pixelQueryParameters {
                    id
                    pixelId
                    parameterName
                    sourceData
                    constantValue
                }
                pixelSubscribers {
                    id
                    pixelId
                    affiliateId
                    trafficSourceId
                    revenueType
                }
                url
            }
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });

    const [deletePixel, {loading: deletePixelLoading, data: deletePixelData}] = useMutation(gql`
        mutation DeletePixel(
            $id: UUID!
        ) {
            deletePixel(id: $id)
        }
    `, {
        context: {
            clientName: "kgraphql"
        }
    });

    const findOptionName = (options, id) => {
        const option = options.find(item => item.id === id)
        return option ? `${option.name} (${id})` : null
    }

    const findOptionId = (options, name) => {
        const option = options.find(item => item.name === name)
        return option ? option.id : null
    }

    const setData = (objData, objKey) => {
        pixelMap.forEach(section => {
            section.arr.forEach((item => {
                const {key, type} = item
                if (inputs[item.key] === undefined) {
                    const dataKey = objData[objKey][key];
                    let value = dataKey === '' ? '' : dataKey;

                    if (_.isObject(value)) {
                        value = value.enum;
                    }

                    setInputs(inputs => ({
                        ...inputs,
                        [key]: value
                    }));
                    setDefaultValues(inputs => ({
                        ...inputs,
                        [key]: value
                    }));
                }
            }));
        });

        const queryParameters = data.pixel.pixelQueryParameters;
        if (queryParameters.length) {
            let finalArr = [];
            queryParameters.forEach(item => {
                let obj = {};
                let arr = [];
                pixelQueryParametersMap.forEach(row => {
                    obj = {
                        ...row,
                        value: item[row.key]
                    }
                    arr.push(obj);
                });
                arr.push({
                    id: item.id,
                    key: 'id'
                }, {
                    id: item.pixelId,
                    key: 'pixelId'
                }, {
                    delete: false,
                    key: 'delete'
                });
                finalArr.push(arr);
            });

            setQueryParameters(finalArr);
        }

        const subscribers = data.pixel.pixelSubscribers;
        if (subscribers.length) {
            let finalArr = [];
            subscribers.forEach(item => {
                let obj = {};
                let arr = [];
                pixelSubscribersMap.forEach(row => {
                    let value = item[row.key]

                    obj = {
                        ...row,
                        value: value
                    }
                    arr.push(obj);
                });
                arr.push({
                    id: item.id,
                    key: 'id'
                }, {
                    id: item.pixelId,
                    key: 'pixelId'
                }, {
                    delete: false,
                    key: 'delete'
                });
                finalArr.push(arr);
            });

            setSubscribers(finalArr);
        }
    };

    useEffect(() => {
        if (isSaved) {
            setTimeout(() => setIsSaved(false), 5000);
            setManualLoad(false);
            refetch();

        }
    }, [isSaved]);

    useEffect(() => {
        if (data && Object.keys(data).length && data.pixel) {
            setData(data, 'pixel');
        }
    }, [data]);

    useEffect(() => {
        if (mutationLoading) {
            closeModal();
        }
    }, [mutationLoading]);

    useEffect(() => {
        if (mutationData && Object.keys(mutationData).length) {
            setEditView(false);
            closeModal();
            setIsSaved(true);
        }
    }, [mutationData])

    useEffect(() => {
        if (deletePixelData) {
            setTimeout(() => navigate('/info/pixels'), 5000);
        }
    }, [deletePixelData])

    const handleQueryParametersChange = (idx, rowIdx, newVal) => {
        let values = [...queryParameters];
        const obj = [...values[idx]];
        obj[rowIdx] = {
            ...obj[rowIdx],
            value: newVal
        };
        values[idx] = [...obj];
        setQueryParameters(values);
    }

    const handleSubscribersChange = (idx, rowIdx, newVal) => {
        let values = [...subscribers];
        const obj = [...values[idx]];
        obj[rowIdx] = {
            ...obj[rowIdx],
            value: newVal
        };
        values[idx] = [...obj];
        setSubscribers(values);
    }

    const deleteQueryParametersRow = (deleteIndex) => {
        const values = [...queryParameters];
        let obj = [...values[deleteIndex]];
        const newSet = obj.map(item => {
            if (item.key === 'delete') {
                item.delete = true;
            }
            return {
                ...item,
            };
        });
        values[deleteIndex] = [...newSet];
        setQueryParameters(values);
    };

    const deleteSubscribersRow = (deleteIndex) => {
        const values = [...subscribers];
        let obj = [...values[deleteIndex]];
        const newSet = obj.map(item => {
            if (item.key === 'delete') {
                item.delete = true;
            }
            return {
                ...item,
            };
        });
        values[deleteIndex] = [...newSet];
        setSubscribers(values);
    };

    const addQueryParametersRow = () => {
        const values = [...queryParameters];
        const newSet = pixelQueryParametersMap.map(item => {
            return {
                ...item,
                value: ''
            };
        });
        values.push(newSet);
        setQueryParameters(values);
    };

    const addSubscribersRow = () => {
        const values = [...subscribers];
        const newSet = pixelSubscribersMap.map(item => {
            return {
                ...item,
                value: ''
            };
        });
        values.push(newSet);
        setSubscribers(values);
    };

    const readOnlyValueGenerator = (item) => {
        if (item.key === 'affiliateId') {
            return findOptionName(data.allAffiliate.nodes, item.value);
        }

        if (item.key === 'trafficSourceId') {
            return findOptionName(data.allTrafficSource.nodes, item.value);
        }

        return _.isNil(item.value) || item.value === '' ? 'N/A' : item.value
    }

    if (loading || manualLoad) {
        return (
            <Backdrop className={classes.backdrop} open={true}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        );
    }

    if (error || !data.pixel) {
        return (
            <Paper className={classes.paper}>
                <Alert severity="error">Well, this in embarrassing&hellip; something went wrong.</Alert>
                <p>
                    <a href="/#" onClick={(e) => {
                        e.preventDefault();
                        navigate('/info/pixels');
                    }}>Click here to view all companies.</a>
                </p>
            </Paper>
        );
    }

    return (
        <>
            <Typography variant="h2" component="h2" className={classes.monospace}>
                Pixel - {data.pixel.pixelName || 'N/A'}
            </Typography>
            {isSaved ? (
                <Paper className={classes.paper}>
                    <Alert severity="success">Updates Saved!</Alert>
                </Paper>
            ) : false}
            {isError ? (
                <Paper className={classes.paper}>
                    <Alert severity="error">Please add values to the highlighted fields!</Alert>
                </Paper>
            ) : false}
            {data ? (
                <Paper className={classes.paper}>
                    <Container maxWidth={false}>
                        <form id="pixel-form">
                            {env === 'test' ? (
                                <Box className={trafficClasses.headerBox}>
                                    <Grid container alignItems="flex-end">
                                        <Grid item xs={12} style={{minHeight: '100%'}}
                                              className={`${trafficClasses.header} ${trafficClasses.headerButtons} `}>
                                            {!editView ? (
                                                <>
                                                    <Button variant="contained" color="secondary"
                                                            onClick={() => setEditView(true)}>
                                                        Edit
                                                    </Button>
                                                    <Button variant="outlined" className={classes.deleteButton}
                                                            onClick={() => openModal('delete')}
                                                            startIcon={<DeleteIcon/>}>
                                                        Delete
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                                                        Save Changes
                                                    </Button>
                                                    <Button variant="contained" color="secondary" onClick={() => {
                                                        refetch()
                                                        setEditView(false);
                                                    }}>
                                                        Cancel
                                                    </Button>
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : null}
                            <Box className={trafficClasses.headerBox}>
                                <Grid container spacing={3} alignItems="flex-end">
                                    {pixelMap.map((section, idx) => (
                                        section.title &&
                                        <PanelBox
                                            key={`${section.title} -${idx.toString()} `}
                                            panelData={section}
                                            trafficClasses={trafficClasses}
                                            editView={editView}
                                            inputs={inputs}
                                            data={data}
                                            handleFilterChange={handleFilterChange}
                                            handleInputChange={handleInputChange}
                                            handleSwitchChange={handleSwitchChange}
                                            setInputs={setInputs}
                                            rowCol={12}
                                        />
                                    ))}
                                </Grid>
                            </Box>
                            <Divider style={{margin: '30px 0'}}/>
                            <ExpanderList
                                listTitle={'Pixel Query Parameters'}
                                listType={'Query Parameter'}
                                idPrefix={'queryParameters'}
                                editView={editView}
                                handleFieldChange={handleQueryParametersChange}
                                handleSwitchChage={handleSwitchChange}
                                fieldData={queryParameters}
                                graphqlData={data}
                                inputs={inputs}
                                setInputs={setInputs}
                                addRow={addQueryParametersRow}
                                deleteRow={deleteQueryParametersRow}
                            />
                            <Divider style={{margin: '30px 0'}}/>
                            <ExpanderList
                                listTitle={'Pixel Subscribers'}
                                listType={'Subscriber'}
                                idPrefix={'subscribers'}
                                editView={editView}
                                handleFieldChange={handleSubscribersChange}
                                handleSwitchChage={handleSwitchChange}
                                fieldData={subscribers}
                                graphqlData={data}
                                inputs={inputs}
                                setInputs={setInputs}
                                addRow={addSubscribersRow}
                                deleteRow={deleteSubscribersRow}
                                readOnlyValueGenerator={readOnlyValueGenerator}
                            />
                        </form>
                    </Container>
                    <Modal
                        open={isModalOpen === 'edit'}
                        onClose={closeModal}
                    >
                        <Container className={`${classes.modal} ${trafficClasses.modal}`} maxWidth={false}>
                            <Typography variant="h5" align="center" gutterBottom>
                                Please confirm your pixel changes
                            </Typography>
                            <Paper className={classes.paper} style={{marginBottom: 0}}>
                                <Box>
                                    <Grid container className="row-grid">
                                        <Grid item xs={4}/>
                                        <Grid item xs={4}>
                                            <Typography variant="subtitle1" align="center">
                                                CURRENT CHANGES
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant="subtitle1" align="center">
                                                NEW CHANGES
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Box>
                                    {pixelMap.map((section, idx) => (
                                        <RowChanges key={section.title} rowData={section} inputs={inputs}
                                                    defaultValues={defaultValues}/>
                                    ))}
                                </Box>
                                <Grid item xs className={trafficClasses.headerButtons}>
                                    <Button variant="contained" color="primary" onClick={(e) => {
                                        e.preventDefault();

                                        let finalInputs = _.pickBy(inputs, v => v !== '');

                                        let finalQueryParameters = []
                                        queryParameters.forEach((rows) => {
                                            let obj = {};
                                            rows.forEach((item) => {
                                                if (item.key === 'id') {
                                                    obj = {
                                                        ...obj,
                                                        id: item.id,
                                                    };
                                                } else if (item.key === 'pixelId') {
                                                    obj = {
                                                        ...obj,
                                                        pixelId: item.id,
                                                    };

                                                } else if (item.value !== '' && item.key !== 'id') {
                                                    obj = {
                                                        ...obj,
                                                        [item.key]: item.value
                                                    };
                                                }

                                                obj = {
                                                    ...obj,
                                                    delete: item.delete || false
                                                };
                                            });
                                            if (Object.keys(obj).length) {
                                                finalQueryParameters.push(obj);
                                            }
                                        });

                                        let finalSubscribers = []
                                        subscribers.forEach((rows) => {
                                            let obj = {};
                                            rows.forEach((item) => {
                                                if (item.key === 'id') {
                                                    obj = {
                                                        ...obj,
                                                        id: item.id,
                                                    };
                                                } else if (item.key === 'pixelId') {
                                                    obj = {
                                                        ...obj,
                                                        pixelId: item.id,
                                                    };
                                                } else if (item.key === 'affiliateId' && item.value && item.value.length > 0) {
                                                    const affiliateId = item.value
                                                    obj = {
                                                        ...obj,
                                                        affiliateId: affiliateId
                                                    };
                                                } else if (item.key === 'trafficSourceId' && item.value && item.value.length > 0) {
                                                    const trafficSourceId = item.value
                                                    obj = {
                                                        ...obj,
                                                        trafficSourceId: trafficSourceId
                                                    };
                                                } else if (item.key === 'id') {
                                                    obj = {
                                                        ...obj,
                                                        id: item.id,
                                                    };
                                                } else if (item.key === 'pixelId') {
                                                    obj = {
                                                        ...obj,
                                                        pixelId: item.pixelId,
                                                    };
                                                } else if (item.value !== '' && item.key !== 'id') {
                                                    obj = {
                                                        ...obj,
                                                        [item.key]: item.value
                                                    };
                                                }

                                                obj = {
                                                    ...obj,
                                                    delete: item.delete || false
                                                };
                                            });
                                            if (Object.keys(obj).length) {
                                                finalSubscribers.push(obj);
                                            }
                                        });

                                        setManualLoad(true);

                                        const mutator = {
                                            ...finalInputs,
                                            id,
                                            pixelQueryParameters: finalQueryParameters,
                                            pixelSubscribers: finalSubscribers
                                        };

                                        createPixel({
                                            variables: {
                                                input: mutator
                                            }
                                        })
                                    }}>
                                        Save
                                    </Button>
                                    <Button variant="outlined" onClick={() => closeModal()}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Paper>
                        </Container>
                    </Modal>
                    <Modal
                        open={isModalOpen === 'delete'}
                        onClose={closeModal}
                    >
                        <Container className={`${classes.modal}`} maxWidth={false}>
                            <Paper className={classes.paper} style={{marginBottom: 0}}>
                                <Typography variant="h5" align="center" gutterBottom>
                                    Are you sure you want to delete this pixel?
                                </Typography>
                                <Grid item xs className={trafficClasses.headerButtons}
                                      style={{textAlign: 'center', margin: '1rem 0'}}>
                                    <Button variant="contained" color="primary" onClick={(e) => {
                                        e.preventDefault();
                                        setManualLoad(true);
                                        deletePixel({
                                            variables: {
                                                id
                                            }
                                        });
                                    }}>
                                        Confirm
                                    </Button>
                                    <Button variant="outlined" onClick={() => closeModal()}>
                                        Cancel
                                    </Button>
                                </Grid>
                            </Paper>
                        </Container>
                    </Modal>
                </Paper>
            ) : false}
        </>
    );
};

export default Pixel;
